
// frontend/src/pages/TeamPage.js
// Elite Team Detail Page - SEO-Optimized with TheSportsDB data
import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { MainNavigation } from '@/components/MainNavigation';
import { Footer } from '@/components/Footer';
import { GridBackground } from '@/components/GridBackground';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useTeamData } from '@/services/SportsDataService';
import { getLeagueConfig } from '@/components/Sports/SportsConfig';
import { IPTVFunnel } from '@/components/Sports/IPTVFunnel';
import {
    ArrowLeft, Trophy, Calendar, MapPin, Users, Globe,
    Tv, ExternalLink, Twitter, Facebook, Instagram, Youtube
} from 'lucide-react';

// Info Card Component
const InfoCard = ({ icon: Icon, label, value }) => {
    if (!value) return null;
    return (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10">
            <Icon className="w-5 h-5 text-primary flex-shrink-0" />
            <div>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-sm font-medium text-white">{value}</p>
            </div>
        </div>
    );
};

// Social Link Component
const SocialLink = ({ url, icon: Icon, label }) => {
    if (!url) return null;
    const fullUrl = url.startsWith('http') ? url : `https://${url}`;
    return (
        <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-muted-foreground hover:text-white transition-colors"
        >
            <Icon className="w-4 h-4" />
            <span className="text-xs">{label}</span>
        </a>
    );
};

export const TeamPage = () => {
    const { sportSlug, leagueSlug, teamSlug } = useParams();

    // Get league config for breadcrumbs
    const leagueConfig = getLeagueConfig(leagueSlug);

    // Fetch team data using the slug
    const { team, isLoading, isError } = useTeamData(teamSlug, leagueSlug);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [teamSlug]);

    // Loading State
    if (isLoading) {
        return (
            <div className="min-h-screen relative">
                <MainNavigation />
                <GridBackground />
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                        <p className="text-muted-foreground">Loading team data...</p>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // Error or Not Found
    if (isError || !team) {
        return (
            <div className="min-h-screen relative">
                <MainNavigation />
                <GridBackground />
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="text-center">
                        <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                        <p className="text-xl font-bold text-white mb-2">Team Not Found</p>
                        <p className="text-muted-foreground mb-4">We couldn't find this team.</p>
                        <Link to="/sports" className="text-primary hover:underline">
                            ← Back to Sports Hub
                        </Link>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // Get team color for accents
    const teamColor = team.colors?.[0] ? `#${team.colors[0].replace('#', '')}` : '#10b981';
    const teamName = team.name || team.fullName || 'Team';
    const leagueName = team.league || leagueConfig?.name || 'Sports';

    return (
        <div className="min-h-screen relative">
            <Helmet>
                <title>{teamName} | {leagueName} Live Scores & Schedule | XtreamGate Sports</title>
                <meta name="description" content={`Watch ${teamName} games live! Get ${teamName} scores, schedule, stadium info, and streaming guide. ${team.description?.slice(0, 100) || ''}`} />
                <meta name="keywords" content={`${teamName} live stream, ${teamName} schedule, ${teamName} IPTV, watch ${teamName} online, ${leagueName} streaming`} />
                <link rel="canonical" href={`https://xtreamgate.com/sports/${sportSlug}/${leagueSlug}/team/${teamSlug}`} />

                {/* Open Graph */}
                <meta property="og:title" content={`${teamName} | Live Scores & Schedule`} />
                <meta property="og:description" content={`Watch ${teamName} live in 4K. Stadium info, schedule, and streaming guide.`} />
                <meta property="og:image" content={team.logo || team.banner || 'https://xtreamgate.com/og-sports.png'} />
                <meta property="og:url" content={`https://xtreamgate.com/sports/${sportSlug}/${leagueSlug}/team/${teamSlug}`} />

                {/* SportsTeam Schema */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SportsTeam",
                        "name": teamName,
                        "alternateName": team.abbreviation,
                        "logo": team.logo,
                        "sport": sportSlug,
                        "location": {
                            "@type": "Place",
                            "name": team.stadium,
                            "address": team.city
                        },
                        "memberOf": {
                            "@type": "SportsOrganization",
                            "name": leagueName
                        },
                        "url": `https://xtreamgate.com/sports/${sportSlug}/${leagueSlug}/team/${teamSlug}`
                    })}
                </script>
            </Helmet>

            <MainNavigation />
            <GridBackground />

            {/* Breadcrumbs */}
            <nav className="relative z-10 py-4 px-4">
                <div className="max-w-7xl mx-auto">
                    <ol className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                        <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                        <li>/</li>
                        <li><Link to="/sports" className="hover:text-primary transition-colors">Sports</Link></li>
                        {leagueConfig && (
                            <>
                                <li>/</li>
                                <li><Link to={`/sports/${sportSlug}/${leagueSlug}`} className="hover:text-primary transition-colors">{leagueConfig.name}</Link></li>
                            </>
                        )}
                        <li>/</li>
                        <li className="text-primary font-medium">{teamName}</li>
                    </ol>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative z-10 py-8 md:py-12 px-4">
                <div className="max-w-7xl mx-auto">
                    {/* Banner Image */}
                    {team.banner && (
                        <div className="relative h-48 md:h-64 rounded-2xl overflow-hidden mb-8">
                            <img
                                src={team.banner}
                                alt={`${teamName} banner`}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                        </div>
                    )}

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-col md:flex-row items-center gap-6 md:gap-8"
                    >
                        {/* Team Logo */}
                        <div
                            className="w-28 h-28 md:w-36 md:h-36 rounded-3xl flex items-center justify-center p-4 flex-shrink-0"
                            style={{
                                background: `linear-gradient(135deg, ${teamColor}20, transparent)`,
                                border: `2px solid ${teamColor}40`
                            }}
                        >
                            {team.logo ? (
                                <img
                                    src={team.logo}
                                    alt={teamName}
                                    className="w-full h-full object-contain"
                                />
                            ) : (
                                <Trophy className="w-16 h-16 text-muted-foreground" />
                            )}
                        </div>

                        {/* Team Info */}
                        <div className="text-center md:text-left flex-1">
                            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2">{teamName}</h1>
                            <p className="text-lg md:text-xl text-muted-foreground mb-4">{leagueName}</p>

                            <div className="flex flex-wrap justify-center md:justify-start gap-3">
                                {team.city && (
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full">
                                        <MapPin className="w-3 h-3 text-muted-foreground" />
                                        <span className="text-xs text-muted-foreground">{team.city}</span>
                                    </div>
                                )}
                                {team.founded && (
                                    <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-full">
                                        <Calendar className="w-3 h-3 text-muted-foreground" />
                                        <span className="text-xs text-muted-foreground">Est. {team.founded}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Watch CTA */}
                        <a
                            href="https://xtreamcube.shop/freetrialiptv/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-bold rounded-xl shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:scale-105 transition-all"
                        >
                            <Tv className="w-5 h-5" />
                            Watch {team.abbreviation || teamName} Live
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-4 pb-24">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-8">

                        {/* About Section */}
                        {team.description && (
                            <motion.section
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6"
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-1 h-8 bg-gradient-to-b from-primary to-blue-500 rounded-full" />
                                    <h2 className="text-xl font-bold text-white">About {teamName}</h2>
                                </div>
                                <p className="text-muted-foreground leading-relaxed">{team.description}</p>
                            </motion.section>
                        )}

                        {/* Stadium Section */}
                        {team.stadium && (
                            <motion.section
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden"
                            >
                                {team.stadiumImage && (
                                    <div className="h-48 relative">
                                        <img
                                            src={team.stadiumImage}
                                            alt={team.stadium}
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                                        <div className="absolute bottom-4 left-4">
                                            <h3 className="text-xl font-bold text-white">{team.stadium}</h3>
                                            {team.stadiumCapacity && (
                                                <p className="text-sm text-muted-foreground">
                                                    Capacity: {parseInt(team.stadiumCapacity).toLocaleString()}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {!team.stadiumImage && (
                                    <div className="p-6">
                                        <div className="flex items-center gap-3 mb-4">
                                            <MapPin className="w-5 h-5 text-primary" />
                                            <h3 className="text-lg font-bold text-white">{team.stadium}</h3>
                                        </div>
                                        {team.stadiumCapacity && (
                                            <p className="text-sm text-muted-foreground">
                                                Capacity: {parseInt(team.stadiumCapacity).toLocaleString()}
                                            </p>
                                        )}
                                    </div>
                                )}
                                {team.stadiumDescription && (
                                    <div className="p-6 pt-4 border-t border-white/5">
                                        <p className="text-sm text-muted-foreground leading-relaxed">
                                            {team.stadiumDescription.slice(0, 500)}...
                                        </p>
                                    </div>
                                )}
                            </motion.section>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">

                        {/* Quick Info */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="space-y-3"
                        >
                            <InfoCard icon={Trophy} label="League" value={leagueName} />
                            <InfoCard icon={MapPin} label="Location" value={team.city} />
                            <InfoCard icon={Calendar} label="Founded" value={team.founded} />
                            <InfoCard icon={Users} label="Stadium" value={team.stadium} />
                            {team.website && (
                                <InfoCard icon={Globe} label="Website" value={team.website} />
                            )}
                        </motion.div>

                        {/* Social Links */}
                        {(team.twitter || team.facebook || team.instagram || team.youtube) && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="rounded-2xl border border-white/10 bg-white/5 p-4"
                            >
                                <h4 className="text-sm font-bold text-white mb-3">Follow {teamName}</h4>
                                <div className="flex flex-wrap gap-2">
                                    <SocialLink url={team.twitter} icon={Twitter} label="Twitter" />
                                    <SocialLink url={team.facebook} icon={Facebook} label="Facebook" />
                                    <SocialLink url={team.instagram} icon={Instagram} label="Instagram" />
                                    <SocialLink url={team.youtube} icon={Youtube} label="YouTube" />
                                </div>
                            </motion.div>
                        )}

                        {/* Watch CTA Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="rounded-2xl border border-primary/30 bg-primary/10 p-6 text-center"
                        >
                            <Tv className="w-10 h-10 text-primary mx-auto mb-3" />
                            <h4 className="text-lg font-bold text-white mb-2">Watch {teamName} Live</h4>
                            <p className="text-sm text-muted-foreground mb-4">
                                Stream every {teamName} game in 4K with no blackouts
                            </p>
                            <a
                                href="https://xtreamcube.shop/freetrialiptv/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-full py-3 bg-primary text-primary-foreground font-bold rounded-lg hover:bg-primary/90 transition-colors"
                            >
                                Get Free Trial
                            </a>
                        </motion.div>
                    </div>
                </div>

                {/* IPTV CTA Banner */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="mt-12"
                >
                    <IPTVFunnel leagueName={leagueName} teamName={teamName} />
                </motion.section>
            </div>

            <Footer />
        </div>
    );
};
